/**
 * Base Authentication Method Card Component
 *
 * Shared template component for authentication method cards.
 * Provides consistent header layout (icon, title, description, security indicator, toggle)
 * and accepts configuration section as children.
 */

import React from 'react';
import {
  makeStyles,
  tokens,
  Title3,
  Body1,
  Switch,
  Button,
  shorthands,
} from '@fluentui/react-components';
import { SecurityStrengthIndicator } from '../../SelfService/SecurityStrengthIndicator';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    ...shorthands.borderWidth('2px'),
    ...shorthands.borderStyle('solid'),
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    transitionProperty: 'border-color',
    transitionDuration: '0.2s',
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap(tokens.spacingHorizontalL),
  },
  largeIcon: {
    fontSize: '48px',
    width: '48px',
    height: '48px',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    '& > svg': {
      width: '48px',
      height: '48px',
    },
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    flex: 1,
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
});

interface BaseAuthenticationMethodCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  securityLevel: number;
  securityText: string;
  isEnabled: boolean;
  onToggle: (checked: boolean) => void;
  showConfigureButton?: boolean;
  onConfigure?: () => void;
  children?: React.ReactNode;
}

export const BaseAuthenticationMethodCard: React.FC<BaseAuthenticationMethodCardProps> = ({
  icon,
  title,
  description,
  securityLevel,
  securityText,
  isEnabled,
  onToggle,
  showConfigureButton = false,
  onConfigure,
  children,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.card} role="region">
      <div className={styles.cardHeader}>
        <div className={styles.largeIcon} aria-label={`${title} icon`}>
          {icon}
        </div>
        <div className={styles.headerContent}>
          <Title3>{title}</Title3>
          <Body1>{description}</Body1>
          <SecurityStrengthIndicator level={securityLevel} securityText={securityText} />
        </div>
        {/* Configure button - shown when enabled, not editing, and showConfigureButton is true */}
        {isEnabled && showConfigureButton && onConfigure && (
          <Button onClick={onConfigure}>Configure</Button>
        )}
        <div className={styles.toggleContainer}>
          <Switch
            checked={isEnabled}
            onChange={(_, data) => onToggle(data.checked)}
            label={isEnabled ? 'Enabled' : 'Disabled'}
            aria-label={`Enable ${title}`}
          />
        </div>
      </div>

      {/* Configuration section (children) */}
      {children}
    </div>
  );
};
